import React, { useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import SimpleDatePicker from '../../../ui/simpleDatePicker';

const buildPlaceForm = (order) => ({
  supplierId: order?.supplier?.id || '',
  expectedDate: order?.expectedDate || '',
  notes: order?.notes || ''
});

const buildEditForm = (order) => ({
  supplierId: order?.supplier?.id || '',
  brandName: order?.brand?.name || '',
  modelNumber: order?.modelNumber || '',
  frameColor: order?.frameColor || '',
  sizeCode: order?.sizeCode || '',
  frameType: order?.frameType || '',
  expectedDate: order?.expectedDate || '',
  notes: order?.notes || '',
  locationName: order?.location?.name || ''
});

export default function NewOrdersTab({
  newOrders,
  suppliers,
  isLoading,
  isSubmitting,
  searchText,
  onSearchTextChange,
  updateQueuedOrder,
  placeQueuedOrder
}) {
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [placeForm, setPlaceForm] = useState(buildPlaceForm(null));
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState(buildEditForm(null));

  const emptyStateMessage = useMemo(() => {
    if (isLoading) {
      return 'Loading queued frame requests...';
    }

    return searchText ? 'No queued frame requests match the current filter.' : 'No queued frame requests are waiting for procurement.';
  }, [isLoading, searchText]);

  const openPlaceDialog = (order) => {
    setSelectedOrder(order);
    setPlaceForm(buildPlaceForm(order));
    setDialogOpen(true);
  };

  const closePlaceDialog = () => {
    setDialogOpen(false);
    setSelectedOrder(null);
    setPlaceForm(buildPlaceForm(null));
  };

  const openEditDialog = (order) => {
    setSelectedOrder(order);
    setEditForm(buildEditForm(order));
    setEditDialogOpen(true);
  };

  const closeEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedOrder(null);
    setEditForm(buildEditForm(null));
  };

  const submitPlaceOrder = () => {
    if (!selectedOrder) {
      return;
    }

    placeQueuedOrder(selectedOrder.id, placeForm, closePlaceDialog);
  };

  const submitEditOrder = () => {
    if (!selectedOrder) {
      return;
    }

    updateQueuedOrder(selectedOrder.id, editForm, closeEditDialog);
  };

  return (
    <>
      <Paper sx={{ p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <Typography variant="subtitle1">New Orders</Typography>
            <Typography variant="body2" color="text.secondary">
              Review invoice-originated frame requests that were saved without placing a supplier order.
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              value={searchText}
              onChange={(event) => onSearchTextChange(event.target.value)}
              placeholder="Invoice, customer, model, brand"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: { md: 'right' } }}>
              {newOrders.length} queued request{newOrders.length === 1 ? '' : 's'}
            </Typography>
          </Grid>
        </Grid>

        <Table size="small" sx={{ mt: 2 }}>
          <TableHead>
            <TableRow>
              <TableCell>Invoice</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Frame</TableCell>
              <TableCell>Brand</TableCell>
              <TableCell>Supplier</TableCell>
              <TableCell>Details</TableCell>
              <TableCell>Qty</TableCell>
              <TableCell>Location</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {newOrders.length === 0 && (
              <TableRow>
                <TableCell colSpan={9}>
                  <Typography variant="body2" color="text.secondary">
                    {emptyStateMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {newOrders.map((order) => (
              <TableRow key={order.id} hover data-testid={`queued-order-row-${order.id}`}>
                <TableCell>{order.invoiceNumber || 'Unknown'}</TableCell>
                <TableCell>{order.customerName || 'Unknown'}</TableCell>
                <TableCell>{order.modelNumber || 'Unknown'}</TableCell>
                <TableCell>{order.brand?.name || 'Not set'}</TableCell>
                <TableCell>{order.supplier?.supplierName || 'Not assigned'}</TableCell>
                <TableCell>{[order.frameColor, order.sizeCode, order.frameType].filter(Boolean).join(' • ') || 'Not set'}</TableCell>
                <TableCell>{order.quantity}</TableCell>
                <TableCell>{order.location?.name || 'Unknown'}</TableCell>
                <TableCell align="right">
                  <Grid container spacing={1} justifyContent="flex-end">
                    <Grid item>
                      <Button variant="outlined" size="small" data-testid={`queued-order-edit-${order.id}`} onClick={() => openEditDialog(order)}>
                        Edit
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button variant="contained" size="small" data-testid={`queued-order-place-${order.id}`} onClick={() => openPlaceDialog(order)}>
                        Place Order
                      </Button>
                    </Grid>
                  </Grid>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={editDialogOpen} onClose={closeEditDialog} maxWidth="sm" fullWidth data-testid="queued-order-edit-dialog">
        <DialogTitle>Edit Queued Order</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                Invoice {selectedOrder?.invoiceNumber || 'Unknown'}
                {selectedOrder?.customerName ? ` • ${selectedOrder.customerName}` : ''}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                size="small"
                label="Supplier"
                inputProps={{ 'data-testid': 'queued-order-edit-supplier' }}
                value={editForm.supplierId}
                onChange={(event) => setEditForm((prev) => ({ ...prev, supplierId: event.target.value }))}
              >
                <MenuItem value="">Not assigned</MenuItem>
                {suppliers.map((supplier) => (
                  <MenuItem key={supplier.id} value={supplier.id}>{supplier.supplierName}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Brand"
                inputProps={{ 'data-testid': 'queued-order-edit-brand' }}
                value={editForm.brandName}
                onChange={(event) => setEditForm((prev) => ({ ...prev, brandName: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Model #"
                inputProps={{ 'data-testid': 'queued-order-edit-model' }}
                value={editForm.modelNumber}
                onChange={(event) => setEditForm((prev) => ({ ...prev, modelNumber: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Color"
                inputProps={{ 'data-testid': 'queued-order-edit-color' }}
                value={editForm.frameColor}
                onChange={(event) => setEditForm((prev) => ({ ...prev, frameColor: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Size"
                inputProps={{ 'data-testid': 'queued-order-edit-size' }}
                value={editForm.sizeCode}
                onChange={(event) => setEditForm((prev) => ({ ...prev, sizeCode: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Type"
                inputProps={{ 'data-testid': 'queued-order-edit-type' }}
                value={editForm.frameType}
                onChange={(event) => setEditForm((prev) => ({ ...prev, frameType: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Location"
                inputProps={{ 'data-testid': 'queued-order-edit-location' }}
                value={editForm.locationName}
                disabled
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SimpleDatePicker
                label="Expected Date"
                value={editForm.expectedDate}
                onChange={(date) => setEditForm((prev) => ({ ...prev, expectedDate: date || '' }))}
                inputTestId="queued-order-edit-expected-date"
                helperTextMode="never"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Procurement Notes"
                inputProps={{ 'data-testid': 'queued-order-edit-notes' }}
                value={editForm.notes}
                onChange={(event) => setEditForm((prev) => ({ ...prev, notes: event.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditDialog}>Cancel</Button>
          <Button onClick={submitEditOrder} variant="contained" disabled={isSubmitting} data-testid="queued-order-edit-submit">
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialogOpen} onClose={closePlaceDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Place Queued Order</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                Invoice {selectedOrder?.invoiceNumber || 'Unknown'}
                {selectedOrder?.customerName ? ` • ${selectedOrder.customerName}` : ''}
                {selectedOrder?.modelNumber ? ` • ${selectedOrder.modelNumber}` : ''}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                size="small"
                label="Supplier"
                value={placeForm.supplierId}
                onChange={(event) => setPlaceForm((prev) => ({ ...prev, supplierId: event.target.value }))}
              >
                <MenuItem value="">Select supplier</MenuItem>
                {suppliers.map((supplier) => (
                  <MenuItem key={supplier.id} value={supplier.id}>{supplier.supplierName}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <SimpleDatePicker
                label="Expected Date"
                value={placeForm.expectedDate}
                onChange={(date) => setPlaceForm((prev) => ({ ...prev, expectedDate: date || '' }))}
                helperTextMode="never"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Procurement Notes"
                value={placeForm.notes}
                onChange={(event) => setPlaceForm((prev) => ({ ...prev, notes: event.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closePlaceDialog}>Cancel</Button>
          <Button onClick={submitPlaceOrder} variant="contained" disabled={isSubmitting}>
            {isSubmitting ? 'Placing...' : 'Place Order'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}